import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import CharterBundleCalendarModal from './CharterBundleCalendarModal'
import { FlightsClassTypes } from 'constants/flight'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  nights: number
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  checkInDate?: string
  className?: string
}

/**
 * Charter Flight Calendar Select for Desktop
 * Uses ModalContext pattern to open the calendar modal
 */
function CharterFlightCalendarSelect(props: Props) {
  const {
    selectedMonth,
    onMonthChange,
    nights,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
    checkInDate,
    className,
  } = props

  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()

  // Set default dates if checkInDate is not set
  useEffect(() => {
    if (!checkInDate) {
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: '2026-04-07',
        checkOut: '2026-04-12',
      })
    }
  }, [checkInDate, dispatch])

  const openCalendarModal = useCallback(async() => {
    const selectedDates = await openModal<{ departDate: string, returnDate: string, checkIn: string, checkOut: string } | undefined>(
      <CharterBundleCalendarModal
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        fareClass={fareClass}
        priceMode={priceMode}
        onDurationChange={onDurationChange}
        durationOptions={durationOptions}
      />
    )

    if (selectedDates) {
      // Update dates in offer page state
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut,
      })
      dispatch({ type: OfferPageActions.closeFilter })
    }
  }, [
    openModal,
    dispatch,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
  ])

  // Display selected date or placeholder
  const value = useMemo(() => {
    if (checkInDate) {
      const checkOut = moment(checkInDate).add(nights, 'days').format('YYYY-MM-DD')
      const dateText = `${intlDateFormatter(checkInDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(checkOut, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> {nightsText}</>
    }
    return undefined
  }, [checkInDate, nights, intlDateFormatter])

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="charter-calendar-select"
      onClick={openCalendarModal}
      icon={<LineCalendarIcon />}
      label="When"
      value={value}
      placeholder="Select your dates"
    />
  )
}

export default CharterFlightCalendarSelect
